import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CButton,
  CSpinner, CFormCheck, CInputGroup, CAlert, CFormInput,
  CFormLabel, CButtonGroup, CListGroup, CListGroupItem, CTooltip
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft, cilPencil, cilCheck, cilX, cilCamera,
  cilChart, cilSync, cilOptions, cilInfo, cilMonitor, cilWarning, cilClock
} from "@coreui/icons";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import enUS from "date-fns/locale/en-US";
import dayjs from "dayjs";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";

// Компоненты
import Loading from "../../components/Loading/Loading";
import RoiEditor from "../../components/RoiEditor/RoiEditor";
import AnalyticsBlock from "../../components/AnalyticsBlock/AnalyticsBlock";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import CameraSettings from "./components/CameraSettings";
import CameraModals from "./components/CameraModals";
import MultiCameraMonitor from "../../components/MultiCameraMonitor/MultiCameraMonitor";
import VisitorAnalyticsBlock from "../../components/VisitorAnalyticsBlock/VisitorAnalyticsBlock";
import InteractiveTimeline from "../../components/InteractiveTimeline/InteractiveTimeline";

// Аудио-компоненты и утилиты
import AudioSearchDashboard from '../../components/AudioSearchDashboard/AudioSearchDashboard';
import AudioDetailModal from '../../components/AudioDetailModal/AudioDetailModal';
import { getAudioSegmentColor } from '../../utils/audioUtils';

// Утилиты и Хуки
import { formatToCameraUTC, formatDuration } from "../../utils/cameraUtils";
import { useCameraAnalytics } from "./hooks/useCameraAnalytics";
import "./CameraDetailPage.css";

// Утилита права
import { getUserRoles } from "../../utils/authUtils";

registerLocale("ru", ru);
registerLocale("en", enUS);

const CameraDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { cameraId } = useParams();
  const location = useLocation();

  // Получаем права
  const { is_staff, is_superuser } = getUserRoles();
  const hasAdminRights = is_staff || is_superuser;

  const [camera, setCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timelineRange, setTimelineRange] = useState(null);
  const [snapshots, setSnapshots] = useState({});
  const [imageSizes, setImageSizes] = useState({ queue: { naturalWidth: 0, naturalHeight: 0 } });
  const [polygons, setPolygons] = useState({ queue: { initial: [], current: [] } });

  const [sourceCameraNames, setSourceCameraNames] = useState({});

  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [minPercentage, setMinPercentage] = useState(5);
  const [activeView, setActiveView] = useState("general");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [settings, setSettings] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [newOverride, setNewOverride] = useState({ date: new Date(), is_working: false, description: "" });

  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ name: "Дневная", start_time: "08:00", end_time: "20:00" });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({ start_time: "12:00", end_time: "13:00", description: "Обед" });

  const [startDate, setStartDate] = useState(dayjs().startOf("day").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("day").toDate());
  const [isLive, setIsLive] = useState(false);
  const [showRoi, setShowRoi] = useState(true);
  const [roiDebugMode, setRoiDebugMode] = useState(false);
  const [roiDebugInfo, setRoiDebugInfo] = useState(null);

  const [monitorConfig, setMonitorConfig] = useState({
    visible: false,
    mode: 'live',
    initialIsoTime: null
  });

  const [recalculateModalVisible, setRecalculateModalVisible] = useState(false);
  const [recalculationDate, setRecalculationDate] = useState(dayjs().startOf("day").toDate());
  const [isRecalculating, setIsRecalculating] = useState(false);

  const [llmModalVisible, setLlmModalVisible] = useState(false);
  const [llmSyncDate, setLlmSyncDate] = useState(dayjs().startOf("day").toDate());
  const [isLlmSyncing, setIsLlmSyncing] = useState(false);
  const [moveModal, setMoveModal] = useState({ visible: false, log: null, targetGroupId: "" });
  const [blurWarningModalVisible, setBlurWarningModalVisible] = useState(false);

  // Стейты для Аудиоаналитики
  const [audioSegments, setAudioSegments] = useState([]);
  const [audioSuggestions, setAudioSuggestions] = useState([]);
  const [selectedAudioSegment, setSelectedAudioSegment] = useState(null);
  const [audioModalVisible, setAudioModalVisible] = useState(false);

  const abortControllersRef = useRef({});
  const failedCamerasRef = useRef({});

  // Подключение обновленного хука аналитики с поддержкой unattendedClientMeta
  const {
    personnelGeneralMeta, chairMeta, phoneMeta, workFlowersMeta, noUniformMeta, fridgeMeta, receiptMeta,
    unattendedClientMeta, dynamicSummary
  } = useCameraAnalytics(analytics, camera, t, minPercentage);

  const getSourceIds = useCallback((cam) => {
    if (!cam) return [];
    let ids = [];
    const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
    if (isCombined) {
      if (cam.parent) ids.push(cam.parent);
      if (cam.additional_cameras && Array.isArray(cam.additional_cameras)) ids.push(...cam.additional_cameras);
      if (cam.secondary_camera && !ids.includes(cam.secondary_camera)) ids.push(cam.secondary_camera);
    } else if (cam.is_duplicate && cam.parent) {
      ids.push(cam.parent);
    } else {
      ids.push(cam.id);
    }
    return [...new Set(ids.filter((id) => id != null))];
  }, []);

  // БЛОКИ ДАННЫХ ДЛЯ ГРАФИКОВ
  const generalData = useMemo(() => {
    if (!analytics || !analytics.general) return null;
    return { ...analytics.general, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const chairData = useMemo(() => {
    if (!analytics || !analytics.chair) return null;
    return { ...analytics.chair, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const phoneData = useMemo(() => {
    if (!analytics || !analytics.phone) return null;
    return { ...analytics.phone, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const workFlowersData = useMemo(() => {
    if (!analytics || !analytics.work_flowers) return null;
    return { ...analytics.work_flowers, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const noUniformData = useMemo(() => {
    if (!analytics || !analytics.no_uniform) return null;
    return { ...analytics.no_uniform, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const fridgeData = useMemo(() => {
    if (!analytics || !analytics.fridge_empty) return null;
    return { ...analytics.fridge_empty, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const unattendedData = useMemo(() => {
    if (!analytics || !analytics.unattended_clients) return null;
    return { ...analytics.unattended_clients, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  const receiptData = useMemo(() => {
    if (!analytics || !analytics.receipts) return null;
    return { ...analytics.receipts, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
  }, [analytics]);

  // ДАННЫЕ ДЛЯ ТАЙМЛАЙНА ВИДЕО
  const activeTimelineData = useMemo(() => {
    if (!analytics) return [];
    if (activeView === "general") return analytics.general?.timeline || [];
    if (activeView === "chair") return analytics.chair?.timeline || [];
    if (activeView === "phone") return analytics.phone?.timeline || [];
    if (activeView === "work_flowers") return analytics.work_flowers?.timeline || [];
    if (activeView === "no_uniform") return analytics.no_uniform?.timeline || [];
    if (activeView === "fridge_empty") return analytics.fridge_empty?.timeline || [];
    if (activeView === "unattended_clients") return analytics.unattended_clients?.timeline || [];
    if (activeView === "receipts") return analytics.receipts?.timeline || [];
    return [];
  }, [analytics, activeView]);

  // ЦВЕТА ДЛЯ ТАЙМЛАЙНА ВИДЕО
  const activeMeta = useMemo(() => {
    if (activeView === "chair") return chairMeta;
    if (activeView === "phone") return phoneMeta;
    if (activeView === "work_flowers") return workFlowersMeta;
    if (activeView === "no_uniform") return noUniformMeta;
    if (activeView === "fridge_empty") return fridgeMeta;
    if (activeView === "unattended_clients") return unattendedClientMeta;
    if (activeView === "receipts") return receiptMeta;
    return personnelGeneralMeta;
  }, [activeView, chairMeta, phoneMeta, workFlowersMeta, noUniformMeta, fridgeMeta, unattendedClientMeta, receiptMeta, personnelGeneralMeta]);

  // ЦВЕТОВАЯ МЕТА ДЛЯ АУДИОДОРОЖКИ
  const audioMeta = useMemo(() => {
    const meta = { default: { label: "Речь", color: "#3399ff" } };
    audioSegments.forEach(seg => {
      meta[seg.id] = { label: seg.context_type, color: getAudioSegmentColor(seg) };
    });
    return meta;
  }, [audioSegments]);

  const fetchNamesForSourceCameras = useCallback((cam) => {
    const ids = getSourceIds(cam);
    const token = localStorage.getItem("token");
    ids.forEach(async (id) => {
      try {
        const res = await axios.get(`/api/cameras/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
        setSourceCameraNames(prev => ({ ...prev, [id]: res.data.name }));
      } catch (e) {
        setSourceCameraNames(prev => ({ ...prev, [id]: `Камера ${id} (Ошибка)` }));
      }
    });
  }, [getSourceIds]);

  const fetchSnapshots = useCallback((cam) => {
    const ids = getSourceIds(cam);
    const token = localStorage.getItem("token");
    const now = Date.now();

    ids.forEach(async (id) => {
      if (failedCamerasRef.current[id] && now - failedCamerasRef.current[id] < 10000) return;

      if (abortControllersRef.current[id]) abortControllersRef.current[id].abort();

      const controller = new AbortController();
      abortControllersRef.current[id] = controller;

      try {
        const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
        const param = isCombined ? `?source_id=${id}` : "";

        const res = await axios.get(`/api/cameras/${cam.id}/snapshot/${param}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 6000,
          signal: controller.signal
        });

        delete failedCamerasRef.current[id];
        const newUrl = URL.createObjectURL(res.data);

        setSnapshots((prev) => {
          if (prev[id]) URL.revokeObjectURL(prev[id]);
          return { ...prev, [id]: newUrl };
        });
      } catch (e) {
        if (!axios.isCancel(e)) failedCamerasRef.current[id] = Date.now();
      }
    });
  }, [getSourceIds]);

  const fetchAudioAnalytics = async (start, end) => {
    if (!camera?.record_audio) return;
    try {
      const token = localStorage.getItem("token");
      const offset = camera?.timezone_offset || 0;
      const reqStartIso = formatToCameraUTC(start, offset);
      const reqEndIso = formatToCameraUTC(end, offset);

      const res = await axios.get(`/api/audio-analytics/?camera_id=${cameraId}&start_datetime=${reqStartIso}&end_datetime=${reqEndIso}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAudioSegments(res.data.segments || []);
      setAudioSuggestions(res.data.suggestions || []);
    } catch (e) {
      console.error("Ошибка загрузки аудиоаналитики", e);
    }
  };

  const fetchOverrides = async () => {
    setLoadingOverrides(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/date-overrides/?camera_id=${cameraId}`, { headers: { Authorization: `Bearer ${token}` } });
      setOverrides(res.data.results);
    } catch (err) { } finally { setLoadingOverrides(false); }
  };

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/cameras/${cameraId}/shifts/`, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (err) { }
  };

  const fetchBreaks = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/cameras/${cameraId}/breaks/`, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (err) { }
  };

  useEffect(() => {
    const fetchCameraData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const camRes = await axios.get(`/api/cameras/${cameraId}/`, { headers: { Authorization: `Bearer ${token}` } });
        const cameraData = camRes.data;
        setCamera(cameraData);

        if (cameraData.operating_mode === "fridge") {
          setActiveView("fridge_empty");
        } else if (cameraData.operating_mode === "receipt") {
          setActiveView("receipts");
        } else {
          setActiveView("general");
        }

        fetchNamesForSourceCameras(cameraData);
        setSettings({
          work_start_time: cameraData.work_start_time?.substring(0, 5) || "08:00",
          work_end_time: cameraData.work_end_time?.substring(0, 5) || "18:00",
          timezone_offset: cameraData.timezone_offset || 0,
          person_roi_threshold: Math.round((cameraData.person_roi_threshold || 0) * 100),
          person_roi_occupancy_threshold: ((cameraData.person_roi_occupancy_threshold || 0) * 100).toFixed(3),
          person_confidence_threshold: ((cameraData.person_confidence_threshold || 0) * 100).toFixed(1),
          workday_monday: cameraData.workday_monday,
          workday_tuesday: cameraData.workday_tuesday,
          workday_wednesday: cameraData.workday_wednesday,
          workday_thursday: cameraData.workday_thursday,
          workday_friday: cameraData.workday_friday,
          workday_saturday: cameraData.workday_saturday,
          workday_sunday: cameraData.workday_sunday,
          operating_mode: cameraData.operating_mode || "personnel_control",
          count_squatting_as_sitting: cameraData.count_squatting_as_sitting,
          enable_blur: cameraData.enable_blur ?? true,
          has_multiple_shifts: cameraData.has_multiple_shifts || false,
        });

        const ids = getSourceIds(cameraData);
        const roi = cameraData.roi_polygon || {};
        const newPolygons = {};
        const newImageSizes = {};

        if (cameraData.is_combined || cameraData.is_combined_camera || (cameraData.additional_cameras && cameraData.additional_cameras.length > 0)) {
          ids.forEach((id) => {
            newPolygons[id] = { initial: roi[id] || [], current: [] };
            newImageSizes[id] = { naturalWidth: 0, naturalHeight: 0 };
          });
        } else {
          const mainId = ids[0];
          if (mainId) {
            newPolygons[mainId] = { initial: Array.isArray(roi) ? roi : [], current: [] };
            newImageSizes[mainId] = { naturalWidth: 0, naturalHeight: 0 };
          }
        }
        setPolygons(newPolygons);
        setImageSizes(newImageSizes);
        fetchSnapshots(cameraData);
        await fetchOverrides();
        await fetchShifts();
        await fetchBreaks();
      } catch (err) {
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchCameraData();
    return () => { Object.values(abortControllersRef.current).forEach(ctrl => ctrl.abort()); }
  }, [cameraId, t, fetchNamesForSourceCameras, fetchSnapshots, getSourceIds]);

  useEffect(() => {
    let intervalId;
    if (isLive && camera) intervalId = setInterval(() => fetchSnapshots(camera), 500);
    return () => clearInterval(intervalId);
  }, [isLive, camera, fetchSnapshots]);

  useEffect(() => {
    let isMounted = true;
    let timerId;

    const fetchRoiDebugInfo = async () => {
      if (!isMounted || !roiDebugMode) return;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/cameras/${cameraId}/roi-debug-info/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          setRoiDebugInfo(res.data);
          timerId = setTimeout(fetchRoiDebugInfo, 2000);
        }
      } catch (err) {
        if (isMounted) {
          timerId = setTimeout(fetchRoiDebugInfo, 5000);
        }
      }
    };

    if (roiDebugMode) {
      setRoiDebugInfo(null);
      fetchRoiDebugInfo();
    } else {
      setRoiDebugInfo(null);
      clearTimeout(timerId);
    }
    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [roiDebugMode, cameraId]);

  const handleImageLoaded = (id, { naturalWidth, naturalHeight }) => {
    setImageSizes((prev) => ({ ...prev, [id]: { naturalWidth, naturalHeight } }));
    setPolygons((prev) => {
      const targetPoly = prev[id];
      if (!targetPoly) return prev;
      const initialRelative = targetPoly.initial || [];
      const absolutePoints = initialRelative.map((p) => ({ x: p.x * naturalWidth, y: p.y * naturalHeight }));
      if (targetPoly.current.length === 0 && initialRelative.length > 0) return { ...prev, [id]: { ...targetPoly, current: absolutePoints } };
      return prev;
    });
  };

  const handleSaveRoi = async () => {
    if (!camera) return;
    const toRelative = (points, size) => (!size || !size.naturalWidth) ? [] : points.map((p) => ({ x: p.x / size.naturalWidth, y: p.y / size.naturalHeight }));
    const ids = getSourceIds(camera);
    let payloadRoi;
    if (camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0)) {
      payloadRoi = {};
      ids.forEach((id) => { payloadRoi[id] = toRelative(polygons[id]?.current || [], imageSizes[id]); });
    } else {
      payloadRoi = toRelative(polygons[ids[0]]?.current || [], imageSizes[ids[0]]);
    }

    try {
      await axios.patch(`/api/cameras/${cameraId}/`, { roi_polygon: payloadRoi }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setPolygons((prev) => {
        const updated = { ...prev };
        ids.forEach((id) => { if (updated[id]) updated[id].initial = toRelative(updated[id].current, imageSizes[id]); });
        return updated;
      });
      toast.success(t("camera.roi_saved"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleResetRoi = (id) => {
    const size = imageSizes[id]?.naturalWidth ? imageSizes[id] : imageSizes[getSourceIds(camera)[0]];
    if (!size || !size.naturalWidth) return;
    setPolygons((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        current: (prev[id]?.initial || []).map((p) => ({ x: p.x * size.naturalWidth, y: p.y * size.naturalHeight }))
      }
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "enable_blur" && !checked) {
      setBlurWarningModalVisible(true);
      return;
    }
    setSettings((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSaveSettings = async () => {
    try {
      const payload = {
        ...settings,
        person_roi_threshold: parseFloat(settings.person_roi_threshold) / 100,
        person_roi_occupancy_threshold: parseFloat(settings.person_roi_occupancy_threshold) / 100,
        person_confidence_threshold: parseFloat(settings.person_confidence_threshold) / 100
      };
      const response = await axios.patch(`/api/cameras/${cameraId}/`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setCamera(response.data);
      toast.success(t("camera.settings_saved_success"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleSegmentDoubleClick = useCallback((itemId, exactTimeIso) => {
    let targetTimeIso = exactTimeIso;

    const rawTimeline = activeTimelineData;
    if (!targetTimeIso && itemId !== null && rawTimeline && rawTimeline[itemId]) {
      targetTimeIso = rawTimeline[itemId].start;
    }

    if (targetTimeIso) {
      setMonitorConfig({
        visible: true,
        mode: 'archive',
        initialIsoTime: targetTimeIso
      });
    }
  }, [activeTimelineData]);

  const fetchReports = async (start = startDate, end = endDate) => {
    if (dayjs(end).isBefore(dayjs(start))) { toast.warn(t("common.error")); return; }
    setLoadingAnalytics(true);
    setAnalytics(null);
    setTimelineRange(null);

    try {
      fetchAudioAnalytics(start, end);

      const token = localStorage.getItem("token");
      const offset = camera?.timezone_offset || 0;
      const reqStartIso = formatToCameraUTC(start, offset);
      const reqEndIso = formatToCameraUTC(end, offset);
      const analyticsParams = new URLSearchParams({ camera_id: cameraId, start_datetime: reqStartIso, end_datetime: reqEndIso }).toString();

      const res = await axios.get(`/api/analytics/?${analyticsParams}`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.date) return toast.warn(t("common.fill_required"));
    const dateString = dayjs(newOverride.date).format("YYYY-MM-DD");
    if (overrides.some((ov) => ov.date === dateString)) return toast.error(t("common.error"));
    try {
      await axios.post("/api/date-overrides/", { camera: cameraId, date: dateString, is_working: newOverride.is_working, description: newOverride.description }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      await fetchOverrides();
      setNewOverride({ date: new Date(), is_working: false, description: "" });
      toast.success(t("camera.exception_added"));
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!window.confirm(t("dashboard.confirm_delete"))) return;
    try {
      await axios.delete(`/api/date-overrides/${overrideId}/`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setOverrides(prev => prev.filter(ov => ov.id !== overrideId));
      toast.success(t("camera.exception_deleted"));
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleSaveName = async () => {
    if (!editingNameValue.trim()) return toast.warn(t("common.fill_required"));
    try {
      await axios.patch(`/api/cameras/${cameraId}/`, { name: editingNameValue }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setCamera(prev => ({ ...prev, name: editingNameValue }));
      setIsEditingName(false);
      toast.success(t("camera.edit_name_success"));
    } catch (error) {
      toast.error(t("common.error"));
    }
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      await axios.post(`/api/cameras/${cameraId}/recalculate-analytics/`, { start_date: formatToCameraUTC(recalculationDate, camera?.timezone_offset || 0) }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success(t("camera.recalc_started", "Пересчет запущен. Это может занять некоторое время."));
      setRecalculateModalVisible(false);
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleAddShift = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/cameras/${cameraId}/shifts/`, newShift, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(prev => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      toast.success("Смена добавлена");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDeleteShift = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/cameras/${cameraId}/shifts/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setShifts(prev => prev.filter(s => s.id !== id));
      toast.success("Смена удалена");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleAddBreak = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/cameras/${cameraId}/breaks/`, newBreak, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(prev => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setNewBreak({ start_time: "", end_time: "", description: "" });
      toast.success("Перерыв добавлен");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDeleteBreak = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/cameras/${cameraId}/breaks/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks(prev => prev.filter(b => b.id !== id));
      toast.success("Перерыв удален");
    } catch (err) { toast.error(t("common.error")); }
  };

  // Склейка чеков
  const receiptEvents = useMemo(() => {
    if (!analytics || !analytics.receipts || !analytics.receipts.timeline) return [];

    const activeSegments = analytics.receipts.timeline.filter(
      evt => evt.class_id !== -110 && evt.class_id !== -1
    );

    if (activeSegments.length === 0) return [];

    const sorted = [...activeSegments].sort((a, b) => new Date(a.start) - new Date(b.start));
    const merged = [];
    let current = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      const currentEndMs = new Date(current.end).getTime();
      const nextStartMs = new Date(next.start).getTime();
      const gapMs = nextStartMs - currentEndMs;

      if (gapMs < 240000) {
        if (new Date(next.end).getTime() > currentEndMs) {
          current.end = next.end;
        }
      } else {
        merged.push(current);
        current = { ...next };
      }
    }
    merged.push(current);
    return merged;
  }, [analytics]);

  const handleReceiptEventClick = useCallback((event) => {
    const startMs = new Date(event.start).getTime();
    const endMs = new Date(event.end).getTime();
    const centerMs = (startMs + endMs) / 2;

    const zoomStart = new Date(centerMs - 30000).toISOString();
    const zoomEnd = new Date(centerMs + 30000).toISOString();

    setTimelineRange({ start: zoomStart, end: zoomEnd });
    handleSegmentDoubleClick(null, event.start);
  }, [handleSegmentDoubleClick]);

  useEffect(() => {
    if (camera && !loading) {
      const queryParams = new URLSearchParams(location.search);
      const audioAlertId = queryParams.get('audio_alert');

      if (audioAlertId) {
        const token = localStorage.getItem('token');
        axios.get(`/api/audio-alerts/${audioAlertId}/`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          const alt = res.data;
          const dummySegment = {
            id: alt.id,
            start_time: alt.timestamp,
            start_sec: alt.start_sec,
            audio_url: alt.audio_url,
            context_type: alt.segment_data?.context_type || 'Неизвестно',
            dialog_markers: alt.segment_data?.dialog_markers || {},
            topics: alt.segment_data?.topics || {},
            raw_lines: alt.raw_lines || []
          };
          setSelectedAudioSegment(dummySegment);
          setAudioModalVisible(true);
        }).catch(e => console.error("Ошибка загрузки аудио-инцидента", e));
      }
    }
  }, [camera, loading, location.search]);


  if (loading) return <Loading name={t("common.loading")} />;
  const sourceCameraIds = getSourceIds(camera);

  const monitorCameras = sourceCameraIds.map(id => ({
    id: id,
    name: sourceCameraNames[id] || `Камера ${id}`,
    timezone_offset: camera.timezone_offset
  }));

  const hintStyle = { borderBottom: '1px dashed #999', cursor: 'help', width: 'fit-content' };

  const isFridge = camera?.operating_mode === "fridge";
  const isReceipt = camera?.operating_mode === "receipt";
  const isVisitorCounting = camera?.operating_mode === "visitor_counting";

  return (
    <div className="bg-light min-vh-100 p-4" style={{ position: "relative" }}>
      <CContainer fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          {isEditingName ? (
            <CInputGroup style={{ maxWidth: "400px" }}>
              <CFormInput
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
              />
              <CButton color="success" onClick={handleSaveName} className="text-white">
                <CIcon icon={cilCheck} />
              </CButton>
              <CButton color="secondary" onClick={() => setIsEditingName(false)}>
                <CIcon icon={cilX} />
              </CButton>
            </CInputGroup>
          ) : (
            <div className="d-flex align-items-center">
              <h2 className="mb-0 me-3">{camera?.name}</h2>
              {hasAdminRights && (
                <CButton color="light" size="sm" onClick={() => { setEditingNameValue(camera.name); setIsEditingName(true); }} title={t("common.edit")}>
                  <CIcon icon={cilPencil} />
                </CButton>
              )}
            </div>
          )}
          <div style={{ display: "flex" }}>
            <CButton color="secondary" as={Link} to="/dashboard">
              <CIcon icon={cilArrowLeft} className="me-2" /> {t("common.back")}
            </CButton>
            <LanguageSwitcher />
          </div>
        </div>

        {camera.is_duplicate && !camera.is_combined && !camera.is_combined_camera && (
          <CAlert color="info" className="mb-4">
            <CIcon icon={cilInfo} className="me-2" />
            {t("camera.child_post_alert", { parentId: camera.parent })}
          </CAlert>
        )}

        {(camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0)) && (
          <CAlert color="success" className="mb-4">
            <CIcon icon={cilInfo} className="me-2" />
            <strong>Совмещенный пост:</strong> {sourceCameraIds.map((id) => sourceCameraNames[id] || `Камера ${id}`).join(" + ")}
          </CAlert>
        )}

        <CRow>
          {/* ЛЕВАЯ КОЛОНКА (Настройки и ROI) */}
          <CCol xl={5} className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex gap-3 align-items-center">
                <CFormCheck id="liveViewCheck" label={t("camera.live_view")} checked={isLive} onChange={() => setIsLive(!isLive)} />
                <CFormCheck id="showRoiCheck" label={t("camera.show_roi")} checked={showRoi} onChange={() => setShowRoi(!showRoi)} />
                <CFormCheck id="debugRoiCheck" label={t("camera.debug_roi")} checked={roiDebugMode} onChange={() => setRoiDebugMode(!roiDebugMode)} />

                {sourceCameraIds.length > 1 && (
                  <CButton color="primary" size="sm" className="ms-2 text-white" onClick={() => setMonitorConfig({ visible: true, mode: 'live', date: new Date(), timeSeconds: 12 * 3600 })}>
                    <CIcon icon={cilMonitor} className="me-1" /> {t('monitor.btn_open')}
                  </CButton>
                )}
              </div>
              <CButton color="light" size="sm" onClick={() => fetchSnapshots(camera)}>
                <CIcon icon={cilCamera} />
              </CButton>
            </div>

            {sourceCameraIds.map((id, index) => (
              <div key={id} className={index === 0 ? "" : "mt-3"}>
                <RoiEditor
                  snapshotUrl={snapshots[id]}
                  polygonPoints={polygons[id] || { initial: [], current: [] }}
                  setPolygonPoints={(np) => setPolygons(p => ({ ...p, [id]: np }))}
                  isLive={isLive}
                  showRoi={showRoi}
                  onSave={handleSaveRoi}
                  onReset={() => handleResetRoi(id)}
                  onClear={() => setPolygons(p => ({ ...p, [id]: { ...p[id], current: [] } }))}
                  cameraName={sourceCameraNames[id] || `Камера ${id}`}
                  onImageLoad={(dim) => handleImageLoaded(id, dim)}
                  readOnly={!hasAdminRights}
                  operatingMode={camera?.operating_mode}
                />
              </div>
            ))}

            {hasAdminRights && (
              <CameraSettings
                camera={camera}
                settings={settings}
                handleSettingsChange={handleSettingsChange}
                handleSaveSettings={handleSaveSettings}
                t={t}
                i18n={i18n}
                overrides={overrides}
                loadingOverrides={loadingOverrides}
                newOverride={newOverride}
                setNewOverride={setNewOverride}
                handleAddOverride={handleAddOverride}
                handleDeleteOverride={handleDeleteOverride}
                shifts={shifts}
                newShift={newShift}
                setNewShift={setNewShift}
                handleAddShift={handleAddShift}
                handleDeleteShift={handleDeleteShift}
                breaks={breaks}
                newBreak={newBreak}
                setNewBreak={setNewBreak}
                handleAddBreak={handleAddBreak}
                handleDeleteBreak={handleDeleteBreak}
              />
            )}
          </CCol>

          {/* ПРАВАЯ КОЛОНКА (Аналитика) */}
          <CCol xl={7}>
            <CCard className="mb-4">
              <CCardHeader className="d-flex justify-content-between align-items-center">
                {t("camera.analytics_archive")}
                {hasAdminRights && (
                  <CButtonGroup>
                    <CButton color="warning" variant="outline" size="sm" onClick={() => setRecalculateModalVisible(true)}>
                      <CIcon icon={cilSync} className="me-1" /> {t("camera.recalc_all")}
                    </CButton>
                  </CButtonGroup>
                )}
              </CCardHeader>
              <CCardBody>
                <div className="bg-light p-3 rounded border mb-4">
                  <CRow className="g-3">
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("common.date_start")}</CFormLabel>
                      <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        showTimeSelect
                        className="form-control w-100"
                        dateFormat="dd.MM.yyyy HH:mm"
                        locale={i18n.language === "ru" ? "ru" : "en"}
                      />
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("common.date_end")}</CFormLabel>
                      <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        showTimeSelect
                        className="form-control w-100"
                        dateFormat="dd.MM.yyyy HH:mm"
                        locale={i18n.language === "ru" ? "ru" : "en"}
                      />
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel className="fw-bold mb-1">{t("multi_cam.min_percentage_label", "Мин. % времени для учета группы")}</CFormLabel>
                      <CInputGroup>
                        <CFormInput type="number" min="0" max="100" value={minPercentage} onChange={(e) => setMinPercentage(e.target.value)} />
                        <span className="input-group-text">%</span>
                      </CInputGroup>
                    </CCol>
                    <CCol md={6} className="d-flex align-items-end">
                      <CButton color="primary" onClick={() => fetchReports()} disabled={loadingAnalytics} className="w-100 py-2 fw-bold">
                        {loadingAnalytics ? <CSpinner size="sm" /> : <><CIcon icon={cilChart} className="me-2" />{t("common.get_report")}</>}
                      </CButton>
                    </CCol>
                  </CRow>
                </div>

                {/* 1. РЕЖИМ ПОДСЧЕТА ПОСЕТИТЕЛЕЙ */}
                {analytics && !loadingAnalytics && isVisitorCounting && (
                  <VisitorAnalyticsBlock
                    analytics={analytics}
                    onSegmentDoubleClick={handleSegmentDoubleClick}
                  />
                )}

                {/* 2. ВСЕ ОСТАЛЬНЫЕ РЕЖИМЫ */}
                {analytics && !loadingAnalytics && dynamicSummary && !isVisitorCounting && (
                  <>
                    <CCard className="mb-4 bg-light shadow-sm border-secondary border-top-5">
                      <CCardHeader className="fw-bold text-uppercase">{t("camera.dynamic_summary_title")}</CCardHeader>
                      <CCardBody>
                        <CRow>
                          <CCol md={6}>
                            <CListGroup flush className="bg-transparent">
                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={isFridge ? "Максимальное количество одновременно пустующих мест" : isReceipt ? "Максимальное количество чеков в кадре" : t("tooltips.max_people")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>
                                    {isFridge ? "Макс. пустых мест" : isReceipt ? "Макс. чеков/касс" : t("camera.max_people_working")}
                                  </span>
                                </CTooltip>
                                <span className="fs-5 fw-bold">{dynamicSummary.maxPeople} {isFridge ? "мест" : isReceipt ? "шт." : t("camera.people_unit")}</span>
                                <span className="ms-2 badge bg-info text-dark">{">"} {minPercentage}% {t("camera.of_time")}</span>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={isFridge ? "Среднее количество пустующих мест за выбранный период" : isReceipt ? "Среднее количество чеков в кадре" : t("tooltips.avg_people")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>
                                    {isFridge ? "Среднее количество пустых мест" : isReceipt ? "Среднее кол-во чеков" : t("camera.avg_people")}
                                  </span>
                                </CTooltip>
                                <span className="fs-5 fw-bold text-primary">{dynamicSummary.weightedAveragePeople.toFixed(2)} {isFridge ? "мест" : isReceipt ? "шт." : t("camera.people_unit")}</span>
                                <small className="d-block text-muted fst-italic">{t("camera.weighted_average")}</small>
                              </CListGroupItem>

                              <CListGroupItem className="bg-transparent ps-0">
                                <CTooltip content={isFridge ? "Время, в течение которого пустых мест не было (все заполнено)" : isReceipt ? "Время, когда чеков в кадре не было обнаружено" : t("tooltips.downtime")}>
                                  <span className="text-muted small d-block text-uppercase" style={hintStyle}>
                                    {isFridge ? "Время: Всё заполнено" : isReceipt ? "Время: Нет чека в кадре" : t("camera.employee_absence")}
                                  </span>
                                </CTooltip>
                                <span className="fs-5 fw-bold">{formatDuration(dynamicSummary.totalDowntime)}</span>
                              </CListGroupItem>

                              {/* Таблица первого и последнего появления сотрудников */}
                              {analytics && analytics.daily_presence && analytics.daily_presence.length > 0 && !isFridge && !isReceipt && (
                                <CCard className="mb-4 shadow-sm border-top-info border-top-3 mt-3">
                                  <CCardHeader className="bg-white">
                                    <strong><CIcon icon={cilInfo} className="me-2" />Учет рабочего времени: появление сотрудников</strong>
                                  </CCardHeader>
                                  <CCardBody className="p-0">
                                    <div className="table-responsive">
                                      <table className="table table-bordered table-hover text-center align-middle mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Дата</th>
                                            <th className="text-success">Первое появление в кадре</th>
                                            <th className="text-danger">Последнее исчезновение</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {analytics.daily_presence.map((row) => (
                                            <tr key={row.date}>
                                              <td className="fw-bold">{dayjs(row.date).format("DD.MM.YYYY")}</td>
                                              <td className="text-success fw-bold fs-5">{row.first_appearance}</td>
                                              <td className="text-danger fw-bold fs-5">{row.last_disappearance}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </CCardBody>
                                </CCard>
                              )}

                              {!isFridge && !isReceipt && (
                                <CListGroupItem className="bg-transparent ps-0">
                                  <CTooltip content="Доля времени, когда сотрудники заняты делом (не сидят и не в телефоне)">
                                    <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.efficiency_activity")}</span>
                                  </CTooltip>
                                  <span className={`fs-5 fw-bold ${dynamicSummary.efficiency > 50 ? "text-success" : "text-danger"}`}>
                                    {dynamicSummary.efficiency.toFixed(1)}%
                                  </span>
                                  <small className="d-block text-muted fst-italic">Отношение полезной активности ко всему раб. времени</small>
                                </CListGroupItem>
                              )}
                            </CListGroup>
                          </CCol>

                          <CCol md={6}>
                            <CListGroup flush className="bg-transparent">
                              {isFridge ? (
                                <>
                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content="Общее накопленное время, когда в холодильнике пустовали места">
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>Общее время пустых мест</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-info">{formatDuration(dynamicSummary.totalFridgeEmpty)}</span>
                                  </CListGroupItem>

                                  {/* НОВЫЙ БЛОК ДЛЯ ХОЛОДИЛЬНИКА: ОЖИДАНИЕ БЕЗ ПЕРСОНАЛА */}
                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content="Суммарное время, когда клиенты находились в холодильнике без сотрудников более 1 минуты">
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>Клиенты без внимания (&gt;1 мин)</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-warning">{formatDuration(dynamicSummary.totalUnattended)}</span>
                                    <small className="d-block text-muted">
                                      Зафиксировано инцидентов: <strong className="text-danger">{dynamicSummary.unattendedIncidents?.length || 0} шт.</strong>
                                    </small>
                                  </CListGroupItem>
                                </>
                              ) : isReceipt ? (
                                <CListGroupItem className="bg-transparent ps-0">
                                  <CTooltip content="Общее время фиксации чека или кассового аппарата в кадре">
                                    <span className="text-muted small d-block text-uppercase" style={hintStyle}>Время присутствия чека/кассы</span>
                                  </CTooltip>
                                  <span className="fs-5 fw-bold text-danger">{formatDuration(dynamicSummary.totalReceipts)}</span>
                                </CListGroupItem>
                              ) : (
                                <>
                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content={t("tooltips.total_work")}>
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.total_work_man_hours")}</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-success">{formatDuration(dynamicSummary.totalWork)}</span>
                                  </CListGroupItem>

                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content={t("tooltips.chair")}>
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.loss_chair")}</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-warning">{formatDuration(dynamicSummary.totalChair)}</span>
                                  </CListGroupItem>

                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content={t("tooltips.phone")}>
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.loss_phone")}</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-danger">{formatDuration(dynamicSummary.totalPhone)}</span>
                                  </CListGroupItem>

                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content="Общее время, потраченное сотрудниками на работу с цветами">
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>Время: Работа с цветами</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-info">{formatDuration(dynamicSummary.totalWorkFlowers)}</span>
                                  </CListGroupItem>

                                  <CListGroupItem className="bg-transparent ps-0">
                                    <CTooltip content="Общее время нахождения на смене без униформы">
                                      <span className="text-muted small d-block text-uppercase" style={hintStyle}>Нарушение формы</span>
                                    </CTooltip>
                                    <span className="fs-5 fw-bold text-danger">{formatDuration(dynamicSummary.totalNoUniform)}</span>
                                  </CListGroupItem>
                                </>
                              )}
                            </CListGroup>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>

                    {/* ПЕРЕКЛЮЧАТЕЛЬ ВКЛАДОК ДЛЯ ХОЛОДИЛЬНИКА */}
                    {isFridge && (
                      <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                        <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> Режим аналитики холодильника:</span>
                        <CFormCheck type="radio" id="viewToggleFridge" name="viewToggle" label="🧊 Пустые места в холодильнике" checked={activeView === "fridge_empty"} onChange={() => setActiveView("fridge_empty")} />
                        <CFormCheck type="radio" id="viewToggleUnattended" name="viewToggle" label="⏳ Клиенты без внимания (>1 мин)" checked={activeView === "unattended_clients"} onChange={() => setActiveView("unattended_clients")} />
                      </div>
                    )}

                    {/* ПЕРЕКЛЮЧАТЕЛЬ ВКЛАДОК ДЛЯ КОНТРОЛЯ ПЕРСОНАЛА */}
                    {!isFridge && !isReceipt && (
                      <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                        <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> {t("camera.view_toggle_label")}</span>
                        <CFormCheck type="radio" id="viewToggleGeneral" name="viewToggle" label={t("camera.view_general")} checked={activeView === "general"} onChange={() => setActiveView("general")} />
                        <CFormCheck type="radio" id="viewToggleChair" name="viewToggle" label={t("camera.view_chair")} checked={activeView === "chair"} onChange={() => setActiveView("chair")} />
                        <CFormCheck type="radio" id="viewTogglePhone" name="viewToggle" label={t("camera.view_phone")} checked={activeView === "phone"} onChange={() => setActiveView("phone")} />
                        <CFormCheck type="radio" id="viewToggleWork" name="viewToggle" label="Работа с цветами" checked={activeView === "work_flowers"} onChange={() => setActiveView("work_flowers")} />
                        <CFormCheck type="radio" id="viewToggleNoUni" name="viewToggle" label="Без формы" checked={activeView === "no_uniform"} onChange={() => setActiveView("no_uniform")} />
                      </div>
                    )}

                    {/* БЛОКИ С ТАЙМЛАЙНОМ */}
                    {!isFridge && !isReceipt && activeView === "general" && generalData && (
                      <AnalyticsBlock title="Общая активность" analyticsData={generalData} classIdToMeta={personnelGeneralMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="general" operatingMode="personnel_control" />
                    )}
                    {!isFridge && !isReceipt && activeView === "chair" && chairData && (
                      <AnalyticsBlock title="Сидя на стуле" analyticsData={chairData} classIdToMeta={chairMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="chair" operatingMode="personnel_control" />
                    )}
                    {!isFridge && !isReceipt && activeView === "phone" && phoneData && (
                      <AnalyticsBlock title="В телефоне" analyticsData={phoneData} classIdToMeta={phoneMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="phone" operatingMode="personnel_control" />
                    )}
                    {!isFridge && !isReceipt && activeView === "work_flowers" && workFlowersData && (
                      <AnalyticsBlock title="Работа с цветами" analyticsData={workFlowersData} classIdToMeta={workFlowersMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="work_flowers" operatingMode="personnel_control" />
                    )}
                    {!isFridge && !isReceipt && activeView === "no_uniform" && noUniformData && (
                      <AnalyticsBlock title="Без формы" analyticsData={noUniformData} classIdToMeta={noUniformMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="no_uniform" operatingMode="personnel_control" />
                    )}

                    {/* ХОЛОДИЛЬНИК: ПУСТЫЕ МЕСТА */}
                    {isFridge && activeView === "fridge_empty" && fridgeData && (
                      <AnalyticsBlock title="Пустые места в холодильнике" analyticsData={fridgeData} classIdToMeta={fridgeMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="fridge_empty" operatingMode="fridge" />
                    )}

                    {/* ХОЛОДИЛЬНИК: КЛИЕНТЫ БЕЗ ВНИМАНИЯ (>1 МИН) */}
                    {isFridge && activeView === "unattended_clients" && unattendedData && (
                      <AnalyticsBlock title="Клиенты в холодильнике без сотрудников (>1 мин)" analyticsData={unattendedData} classIdToMeta={unattendedClientMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="unattended_clients" operatingMode="fridge" />
                    )}

                    {/* КОНТРОЛЬ ЧЕКА */}
                    {isReceipt && activeView === "receipts" && receiptData && (
                      <AnalyticsBlock
                        title="Контроль наличия чека/кассы"
                        analyticsData={receiptData}
                        classIdToMeta={receiptMeta}
                        onSegmentDoubleClick={handleSegmentDoubleClick}
                        type="receipts"
                        operatingMode="receipt"
                        dateRangeOverride={timelineRange}
                      />
                    )}
                  </>
                )}

                {/* ЖУРНАЛ ИНЦИДЕНТОВ: КЛИЕНТЫ БЕЗ ВНИМАНИЯ В ХОЛОДИЛЬНИКЕ */}
                {isFridge && dynamicSummary?.unattendedIncidents?.length > 0 && !isVisitorCounting && (
                  <CCard className="mb-4 shadow-sm border-top-warning border-top-3">
                    <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center">
                      <strong className="fs-5 text-warning d-flex align-items-center">
                        <CIcon icon={cilWarning} className="me-2 text-warning" />
                        Инциденты: Клиенты в холодильнике без внимания (&gt;1 мин) ({dynamicSummary.unattendedIncidents.length} шт.)
                      </strong>
                      <span className="text-muted small">Нажмите на строку для мгновенного просмотра видео</span>
                    </CCardHeader>
                    <CCardBody style={{ maxHeight: "350px", overflowY: "auto" }} className="p-0">
                      <div className="list-group list-group-flush">
                        {dynamicSummary.unattendedIncidents.map((inc, idx) => {
                          const localStart = dayjs(inc.start);
                          const localEnd = dayjs(inc.end);

                          return (
                            <button
                              key={idx}
                              type="button"
                              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3 px-4"
                              onClick={() => handleSegmentDoubleClick(null, inc.start)}
                            >
                              <div className="d-flex align-items-center">
                                <span className="badge bg-warning text-dark p-2 me-3 text-uppercase">
                                  {inc.client_count > 1 ? `${inc.client_count} клиента` : 'Клиент один'}
                                </span>
                                <span className="fs-5 fw-bold text-dark">
                                  {localStart.format("HH:mm:ss")} — {localEnd.format("HH:mm:ss")}
                                </span>
                                <span className="text-muted small ms-3">({localStart.format("DD.MM.YYYY")})</span>
                              </div>
                              <span className="badge bg-danger p-2 rounded-pill fs-6">{inc.duration_sec} сек. ожидания</span>
                            </button>
                          );
                        })}
                      </div>
                    </CCardBody>
                  </CCard>
                )}

                {/* ЖУРНАЛ ВЫДАЧИ ЧЕКОВ */}
                {isReceipt && receiptEvents.length > 0 && !isVisitorCounting && (
                  <CCard className="mb-4 shadow-sm border-top-danger border-top-3">
                    <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center">
                      <strong className="fs-5 text-danger">История выдачи чеков ({receiptEvents.length} шт.)</strong>
                      <span className="text-muted small">Нажмите на строку для быстрого перехода к видеозаписи и приближения таймлайна</span>
                    </CCardHeader>
                    <CCardBody style={{ maxHeight: "350px", overflowY: "auto" }} className="p-0">
                      <div className="list-group list-group-flush">
                        {receiptEvents.map((evt, idx) => {
                          const localStart = dayjs(evt.start);
                          const localEnd = dayjs(evt.end);
                          const duration = Math.round((new Date(evt.end) - new Date(evt.start)) / 1000);

                          return (
                            <button
                              key={idx}
                              type="button"
                              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3 px-4"
                              onClick={() => handleReceiptEventClick(evt)}
                            >
                              <div className="d-flex align-items-center">
                                <span className="badge bg-danger p-2 me-3 text-uppercase">Выдача чека</span>
                                <span className="fs-5 fw-bold text-dark">
                                  {localStart.format("HH:mm:ss")} — {localEnd.format("HH:mm:ss")}
                                </span>
                                <span className="text-muted small ms-3">({localStart.format("DD.MM.YYYY")})</span>
                              </div>
                              <span className="badge bg-secondary p-2 rounded-pill fs-6">{duration} сек.</span>
                            </button>
                          );
                        })}
                      </div>
                    </CCardBody>
                  </CCard>
                )}

                {/* 3. АУДИОАНАЛИТИКА */}
                {camera?.record_audio && (
                  <div className="mt-5 border-top pt-4">
                    <h4 className="text-primary mb-3">🎙️ Аудиодорожка и Аналитика диалогов</h4>

                    <CCard className="mb-4 shadow-sm border-top-info border-top-3">
                      <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
                        <span>Временная шкала речевой активности</span>
                        <small className="text-muted">Двойной клик откроет плеер и подробный разбор</small>
                      </CCardHeader>
                      <CCardBody>
                        {audioSegments.length > 0 ? (
                          <InteractiveTimeline
                            timelineData={audioSegments.map(s => ({
                              start: s.start_time,
                              end: s.end_time,
                              class_id: s.id,
                              people_count: 0
                            }))}
                            dateRange={analytics?.date_range}
                            classIdToMeta={audioMeta}
                            onItemDoubleClick={(itemId) => {
                              const found = audioSegments.find(s => s.id === itemId);
                              if (found) {
                                setSelectedAudioSegment(found);
                                setAudioModalVisible(true);
                              }
                            }}
                          />
                        ) : (
                          <p className="text-muted text-center py-3 m-0">
                            Аудиозаписи за выбранный период не найдены или находятся в процессе обработки.
                          </p>
                        )}
                      </CCardBody>
                    </CCard>

                    <AudioSearchDashboard
                      segments={audioSegments}
                      suggestions={audioSuggestions}
                      onSegmentSelect={(seg) => {
                        setSelectedAudioSegment(seg);
                        setAudioModalVisible(true);
                      }}
                    />

                    <AudioDetailModal
                      visible={audioModalVisible}
                      onClose={() => setAudioModalVisible(false)}
                      segment={selectedAudioSegment}
                      onWatchVideo={(startTimeIso) => {
                        handleSegmentDoubleClick(null, startTimeIso);
                      }}
                    />
                  </div>
                )}

                {!analytics && !loadingAnalytics && <p className="text-center text-muted">{t("camera.select_period_msg")}</p>}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>

      {/* DEBUG DRAGGABLE WINDOW */}
      {roiDebugMode && (
        <Draggable handle=".roi-debug-handle" bounds="parent">
          <div style={{ position: "absolute", top: "150px", left: "50px", width: "350px", maxHeight: "400px", zIndex: 1050, background: "white", border: "1px solid #dee2e6", borderRadius: "0.375rem", display: "flex", flexDirection: "column" }}>
            <div className="roi-debug-handle" style={{ padding: "0.75rem 1rem", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", cursor: "move", display: "flex", justifyContent: "space-between" }}>
              <h6 className="mb-0">{t("camera.roi_debug_title")}</h6>
              <CButton color="secondary" variant="ghost" size="sm" onClick={() => setRoiDebugMode(false)}>
                <CIcon icon={cilX} />
              </CButton>
            </div>
            <div style={{ padding: "1rem", overflowY: "auto", fontFamily: "monospace", fontSize: "13px" }}>
              {!roiDebugInfo ? (
                <div className="text-center"><CSpinner size="sm" /></div>
              ) : roiDebugInfo.no_frames ? (
                <div className="text-muted text-center py-3">Ожидание свежих кадров...<br /><small>(Возможно, камера выключена)</small></div>
              ) : roiDebugInfo.boxes_data.length === 0 ? (
                <div className="text-muted text-center">{t("camera.roi_debug_no_objects", "Объекты не найдены")}</div>
              ) : (
                roiDebugInfo.boxes_data.map((box, index) => (
                  <div key={index} style={{
                    borderLeft: `4px solid ${box.is_in_roi ? "#2eb85c" : "#e55353"}`,
                    paddingLeft: "8px",
                    marginBottom: "8px",
                    color: box.is_in_roi ? "#2eb85c" : "inherit"
                  }}>
                    <strong style={{ textTransform: "uppercase" }}>{box.class_name}</strong>
                    {box.is_in_roi ? " (В зоне)" : ""} <br />
                    Intersection: {(box.area_percentage * 100).toFixed(2)}% <br />
                    Occupancy: {(box.occupancy_percentage * 100).toFixed(3)}%
                  </div>
                ))
              )}
            </div>
          </div>
        </Draggable>
      )}

      {/* Модальные окна */}
      <CameraModals
        t={t}
        i18n={i18n}
        recalculateModalVisible={recalculateModalVisible}
        setRecalculateModalVisible={setRecalculateModalVisible}
        recalculationDate={recalculationDate}
        setRecalculationDate={setRecalculationDate}
        handleRecalculate={handleRecalculate}
        isRecalculating={isRecalculating}
        llmModalVisible={llmModalVisible}
        setLlmModalVisible={setLlmModalVisible}
        llmSyncDate={llmSyncDate}
        setLlmSyncDate={setLlmSyncDate}
        handleLlmSync={() => { }}
        isLlmSyncing={isLlmSyncing}
        moveModal={moveModal}
        setMoveModal={setMoveModal}
        groupedVehicleLogs={[]}
        handleMoveLog={() => { }}
        blurWarningModalVisible={blurWarningModalVisible}
        setBlurWarningModalVisible={setBlurWarningModalVisible}
        handleAcceptBlurDisable={() => { setSettings(p => ({ ...p, enable_blur: false })); setBlurWarningModalVisible(false); }}
      />

      {/* МУЛЬТИ-МОНИТОР */}
      {monitorConfig.visible && (
        <MultiCameraMonitor
          cameras={monitorCameras}
          onClose={() => setMonitorConfig(p => ({ ...p, visible: false }))}
          initialConfig={monitorConfig}
          timelineData={activeTimelineData}
          classIdToMeta={activeMeta}
          dateRange={timelineRange || analytics?.date_range}
        />
      )}

    </div>
  );
};

export default CameraDetailPage;